import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';

import { subscriptionsKey } from './queries';

/**
 * The two writes this surface owns — `[x]` §20.25.
 *
 * **Two calls, because the ledger is two tables.** A sale is a row of
 * `finance__subscriptions` with a state machine, a period and a renewal; a
 * manual grant is a bare window on `finance__entitlements` with none of the
 * three. The operator sees one row action; the wire cannot see one endpoint.
 *
 * **Neither retries and neither is optimistic.** Both end a person's access,
 * and a row that renders as ended before the service agreed is a lie the
 * operator cannot see through. A retry on a write that already succeeded would
 * be worse: the second call is harmless by design, but the operator would be
 * told twice about one act.
 *
 * **The invalidation set carries the revenue report.** Ending a subscription
 * moves a figure on the console entry and on the band directly above this
 * table; a dashboard still counting an ended subscription as live is drift the
 * operator has no way to notice.
 */

function useLedgerInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    // The prefix, not one page's key: the row that just changed may sit on a
    // page under a different narrowing, and every one of them is now stale.
    void queryClient.invalidateQueries({ queryKey: subscriptionsKey() });
    void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'revenue') });
  };
}

/**
 * End one subscription, now.
 *
 * **No body.** The service used to require an `at_period_end` and no longer
 * accepts one: with nothing renewing, «let the paid period run out» is what
 * happens to every subscription this console does not touch, so the only ending
 * left to ask for is the immediate one.
 */
export function useCancelSubscription() {
  const invalidate = useLedgerInvalidation();

  return useMutation({
    mutationFn: ({ subscriptionId }: { subscriptionId: string }) =>
      api.post<CancelledSubscription>('finance', `/admin/subscriptions/${subscriptionId}:cancel`, {}),
    onSuccess: invalidate,
    retry: false,
  });
}

/**
 * Lift one manual grant.
 *
 * No body, because there is one way to lift a window and no variant of it. The
 * service clamps `ends_at` to now and keeps the row, which is why the path ends
 * `:revoke` rather than being a `DELETE` — the grant stays a line of this very
 * table afterwards.
 */
export function useRevokeEntitlement() {
  const invalidate = useLedgerInvalidation();

  return useMutation({
    mutationFn: (entitlementId: string) =>
      api.post<RevokedEntitlement>('finance', `/admin/entitlements/${entitlementId}:revoke`, {}),
    onSuccess: invalidate,
    retry: false,
  });
}

/**
 * What a cancellation answers with.
 *
 * Read for nothing today — the table is re-fetched rather than patched, because
 * a narrowed page may no longer contain the row at all once its state moves.
 * It is declared because the shape is the contract, and a response typed `never`
 * is how the next reader ends up re-deriving it from the network tab.
 */
interface CancelledSubscription {
  id: string;
  state: string;
  current_period_end: string | null;
  ended_at: string | null;
}

/** What a lift answers with. `ends_at` is the moment of the call; `state` reads `EXPIRED`. */
interface RevokedEntitlement {
  id: string;
  state: string;
  ends_at: string | null;
}
